import { NavLink, useNavigate, useParams } from "react-router";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { useHtzSummaryShowQuery } from "@/services/backend/htz/summary/api/summary-api";
import { Item } from "@/services/backend/htz/summary/types/summary";
import { SummaryItemsTable } from "@/services/backend/htz/summary/components/summary-items-table";
import { SummaryItemCreateDialog } from "@/services/backend/htz/summary/components/summary-item-create-dialog";
import { SummaryItemReportCreateDialog } from "@/services/backend/htz/summary/components/summary-item-report-create-dialog";
import { DocumentReferenceNumberById } from "@/services/backend/documents/components/document-reference-number-by-id";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { FieldErrors } from "@/shared/components/field/field";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

export function ItemsRoute() {
  const { processId, summaryId } = useParams();
  const navigate = useNavigate();
  const { data } = useHtzSummaryShowQuery({ id: summaryId! });
  // A summary without items is not ready. Adding an item resets the error.
  const { errors } = useFieldErrors("items");

  const reportPath = (itemId: UUID) =>
    `/processes/htz/${processId}/summaries/${summaryId}/items/${itemId}/report`;

  const renderReportLink = (item: Item) => (
    <NavLink to={reportPath(item.id)} className="underline underline-offset-4">
      <DocumentReferenceNumberById
        serviceKind={GSMServiceKind.HTZ}
        documentKind={GSMDocumentKind.Report}
        documentId={item.reportId}
        showState
      />
    </NavLink>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anlagenberichte")}</CardTitle>
        <CardDescription>
          {t(
            "Hier können Sie neue Anlagenberichte erstellen oder bestehende hinzufügen.",
          )}
        </CardDescription>
        <CardAction>
          {data && (
            <div className="flex gap-2">
              <SummaryItemCreateDialog
                summaryId={data.id}
                processId={data.processId!}
                usedReportIds={data.items.map((item) => item.reportId)}
                disabled={isImmutable(data)}
              />
              <SummaryItemReportCreateDialog
                summaryId={data.id}
                disabled={isImmutable(data)}
                onCreated={(itemId) => navigate(reportPath(itemId))}
              />
            </div>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        <SummaryItemsTable
          id={summaryId!}
          renderReportLink={renderReportLink}
        />
        <FieldErrors errors={errors} />
      </CardContent>
    </Card>
  );
}
